import React from 'react'
import { SectionCard } from '../../components/sections/SectionCard'
import { Check } from 'lucide-react'
import type { PricingTable, PricingTablePlan } from '@/features/subscriptions/types/pricing-table/pricing-table.types'

interface PreviewSectionProps {
  isDetailView?: boolean
  pricingTableData?: PricingTable
  className?: string
}

function PreviewPlanCard({ plan }: { plan: PricingTablePlan }) {
  const isHighlighted = plan.highlighted

  return (
    <div
      className={`rounded-2xl border p-5 flex flex-col ${isHighlighted ? 'border-primary bg-primary text-white shadow-xl' : 'bg-card'}`}
    >
      {isHighlighted && (
        <div className="mb-3 text-center">
          <span className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold">Most Popular</span>
        </div>
      )}
      <p className="font-semibold text-lg">{plan.name}</p>
      <div className="mt-2 mb-4">
        {plan.price === 0
          ? <p className="text-3xl font-bold">Custom</p>
          : <>
              <span className="text-3xl font-bold">
                {plan.currency === 'EUR' ? '€' : plan.currency}{plan.price}
              </span>
              <span className={`text-sm ${isHighlighted ? 'text-white/70' : 'text-muted-foreground'}`}>/{plan.interval}</span>
            </>}
      </div>
      <button
        type="button"
        className={`rounded-lg py-2 text-sm font-medium mb-4 ${isHighlighted ? 'bg-card text-primary' : 'bg-primary text-white'}`}
      >
        {plan.price === 0 ? 'Contact sales' : 'Get started'}
      </button>
      <ul className="space-y-2 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className={`flex items-start gap-2 text-sm ${isHighlighted ? 'text-white/90' : 'text-muted-foreground'}`}>
            <Check className="h-4 w-4 mt-0.5 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>
    </div>
  )
}

export const PreviewSection = React.memo<PreviewSectionProps>(({
  isDetailView = false,
  pricingTableData,
  className,
}) => {
  const plans = pricingTableData?.plans || []

  if (plans.length === 0) {
    return (
      <SectionCard title="Preview" className={className}>
        <div className="py-8 text-center text-sm text-muted-foreground">
          Add plans to see a preview
        </div>
      </SectionCard>
    )
  }

  return (
    <SectionCard
      title="Preview"
      isCollapsible={!isDetailView}
      className={className}
    >
      <div className="rounded-xl border bg-gradient-to-b from-slate-50 to-white p-6 overflow-x-auto">
        <div className="text-center mb-6">
          <p className="text-2xl font-bold">Choose your plan</p>
          <p className="text-sm text-muted-foreground mt-1">Simple, transparent pricing. No hidden fees.</p>
        </div>
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${Math.min(plans.length, 4)}, 1fr)` }}
        >
          {plans.map(plan => (
            <PreviewPlanCard key={plan.id} plan={plan} />
          ))}
        </div>
      </div>
    </SectionCard>
  )
})

PreviewSection.displayName = 'PreviewSection'
